import React from "react";

/**
 * SVG spiky sphere icon for simulation difficulty presets.
 * Level 0 = smooth sphere (Gentle)
 * Level 1 = small bumps (Moderate)
 * Level 2 = medium spikes (Challenging)
 * Level 3 = many sharp spikes (Custom)
 */
const SpikySphere = ({ level = 0, size = 48 }) => {
  const cx = 50;
  const cy = 50;
  const baseR = 28;

  // Gradient colors — blue tones, darker with higher level
  const gradients = [
    { inner: "#a8d4f5", outer: "#5b9bd5" }, // level 0: light blue
    { inner: "#7bbce8", outer: "#3a7fc1" }, // level 1: medium blue
    { inner: "#5a9fd6", outer: "#2563a0" }, // level 2: deeper blue
    { inner: "#3d85c6", outer: "#1a4a7a" }, // level 3: dark blue
  ];

  const g = gradients[Math.min(level, 3)];

  // Generate spike points around the circle
  const generatePath = () => {
    if (level === 0) {
      // Smooth sphere — just a circle
      return null;
    }

    const spikeConfigs = [
      null, // level 0
      { count: 8, spikeLen: 8, baseWidth: 0.22 }, // level 1: few small bumps
      { count: 14, spikeLen: 13, baseWidth: 0.16 }, // level 2: medium spikes
      { count: 22, spikeLen: 17, baseWidth: 0.11 }, // level 3: many sharp spikes
    ];

    const config = spikeConfigs[Math.min(level, 3)];
    const { count, spikeLen, baseWidth } = config;
    const points = [];

    for (let i = 0; i < count; i++) {
      const angle = (2 * Math.PI * i) / count;
      const halfBase = baseWidth * Math.PI;

      // Base points on circle
      const a1 = angle - halfBase;
      const a2 = angle + halfBase;
      const bx1 = cx + baseR * Math.cos(a1);
      const by1 = cy + baseR * Math.sin(a1);
      const bx2 = cx + baseR * Math.cos(a2);
      const by2 = cy + baseR * Math.sin(a2);

      // Spike tip
      const tipR = baseR + spikeLen;
      const tx = cx + tipR * Math.cos(angle);
      const ty = cy + tipR * Math.sin(angle);

      points.push({ bx1, by1, tx, ty, bx2, by2 });
    }

    // Build path: go around circle, inserting spike triangles
    let d = "";
    for (let i = 0; i < points.length; i++) {
      const p = points[i];
      const nextP = points[(i + 1) % points.length];

      if (i === 0) {
        d += `M ${p.bx1},${p.by1} `;
      }
      // Spike triangle
      d += `L ${p.tx},${p.ty} L ${p.bx2},${p.by2} `;

      // Arc along circle to next spike base
      const angleBetween =
        Math.atan2(nextP.by1 - cy, nextP.bx1 - cx) -
        Math.atan2(p.by2 - cy, p.bx2 - cx);
      const sweepAngle =
        angleBetween < 0 ? angleBetween + 2 * Math.PI : angleBetween;
      const largeArc = sweepAngle > Math.PI ? 1 : 0;
      d += `A ${baseR},${baseR} 0 ${largeArc},1 ${nextP.bx1},${nextP.by1} `;
    }
    d += "Z";
    return d;
  };

  const spikePath = generatePath();

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: "block" }}
    >
      <defs>
        <radialGradient id={`sphere-grad-${level}`} cx="40%" cy="35%" r="60%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.6" />
          <stop offset="40%" stopColor={g.inner} />
          <stop offset="100%" stopColor={g.outer} />
        </radialGradient>
        <radialGradient
          id={`sphere-highlight-${level}`}
          cx="35%"
          cy="30%"
          r="30%"
        >
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Spike shape (behind sphere for shadow effect) or just circle */}
      {spikePath ? (
        <>
          {/* Shadow */}
          <path
            d={spikePath}
            fill="rgba(0,0,0,0.08)"
            transform="translate(2, 2)"
          />
          {/* Filled spiky shape */}
          <path d={spikePath} fill={`url(#sphere-grad-${level})`} />
          {/* Base sphere on top for 3D look */}
          <circle
            cx={cx}
            cy={cy}
            r={baseR - 1}
            fill={`url(#sphere-grad-${level})`}
          />
        </>
      ) : (
        <>
          {/* Shadow */}
          <circle cx={cx + 2} cy={cy + 2} r={baseR} fill="rgba(0,0,0,0.08)" />
          {/* Main sphere */}
          <circle
            cx={cx}
            cy={cy}
            r={baseR}
            fill={`url(#sphere-grad-${level})`}
          />
        </>
      )}

      {/* Highlight reflection */}
      <circle
        cx={cx}
        cy={cy}
        r={baseR - 1}
        fill={`url(#sphere-highlight-${level})`}
      />
    </svg>
  );
};

export default SpikySphere;
